import React, { useMemo, useState } from 'react';
import { BRIEF_FIELD_LIMITS, BRIEF_LABEL_FIELD_KEYS, sanitizeBriefField } from '../../utils/inputSecurity';
import { downloadBriefDocx } from './briefDocxExport';
import {
  BRIEF_FIELDS,
  BRIEF_GROUP_LABELS,
  BRIEF_STEPS,
  BRIEF_TITLE,
  getOptionPillLabel,
} from './briefCopy';
import {
  BUDGET_OPTIONS,
  CREATIVE_OPTIONS,
  DEADLINE_OPTIONS,
  DEFAULT_BRIEF_FORM,
  EXPERIENCE_OPTIONS,
  GENDER_OPTIONS,
  GOAL_OPTIONS,
  KPI_OPTIONS,
  LANDING_OPTIONS,
  MATERIAL_OPTIONS,
  MESSENGER_OPTIONS,
  PIXEL_OPTIONS,
  PLATFORM_OPTIONS,
  SCALE_OPTIONS,
  buildBriefPlainText,
  buildBriefViewModel,
  createBriefFileName,
  getProgressPercent,
} from './briefGeneratorLogic';
import './BriefGenerator.css';

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
    <path
      d="M3 9H2a1 1 0 01-1-1V2a1 1 0 011-1h6a1 1 0 011 1v1"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M7 2v7M4 7l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 12h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const StepHeader = ({ step }) => {
  const meta = BRIEF_STEPS[step - 1];

  return (
    <>
      <div className="brief-step-eyebrow">
        <div className="brief-step-num">{step}</div>
        <div className="brief-step-section">{meta.section}</div>
      </div>
      <h2 className="brief-step-title">{meta.title}</h2>
      <p className="brief-step-hint">{meta.hint}</p>
    </>
  );
};

const StepNav = ({ step, onBack, onNext, nextLabel }) => (
  <div className="brief-nav">
    <button type="button" className="brief-btn-back" onClick={onBack} disabled={step === 1}>
      ← Назад
    </button>
    <button type="button" className="brief-btn-next" onClick={onNext}>
      {nextLabel}
    </button>
  </div>
);

const TextField = ({ fieldKey, value, onChange, multiline = false, hideSublabel = false }) => {
  const field = BRIEF_FIELDS[fieldKey];
  const InputTag = multiline ? 'textarea' : 'input';
  const formFieldKey = BRIEF_LABEL_FIELD_KEYS[fieldKey];
  const maxLength = formFieldKey ? BRIEF_FIELD_LIMITS[formFieldKey] : undefined;

  return (
    <div className="brief-field">
      <label className="brief-field-label" htmlFor={`brief-${fieldKey}`}>
        {field.label}
      </label>
      {!hideSublabel && field.sublabel?.trim() ? (
        <span className="brief-field-sublabel">{field.sublabel}</span>
      ) : null}
      <InputTag
        id={`brief-${fieldKey}`}
        className={multiline ? 'brief-textarea' : 'brief-input'}
        value={value}
        placeholder={field.placeholder}
        onChange={(event) => onChange(event.target.value)}
        maxLength={maxLength}
        autoComplete="off"
        spellCheck={multiline}
        {...(multiline ? { rows: 4 } : { type: fieldKey === 'URL' ? 'url' : 'text' })}
      />
    </div>
  );
};

const MetricsField = ({ averageCheck, margin, onChangeAverage, onChangeMargin }) => (
  <div className="brief-field">
    <div className="brief-field-label">Средний чек и маржинальность</div>
    <span className="brief-field-sublabel">
      Помогает таргетологу понять какой CPL реально допустим
    </span>
    <div className="brief-two-col brief-two-col--metrics">
      <input
        id="brief-AVERAGE_CHECK"
        className="brief-input"
        value={averageCheck}
        placeholder="Средний чек, ₸"
        onChange={(event) => onChangeAverage(event.target.value)}
        maxLength={BRIEF_FIELD_LIMITS.averageCheck}
        autoComplete="off"
      />
      <input
        id="brief-MARGIN"
        className="brief-input"
        value={margin}
        placeholder="Маржинальность, %"
        onChange={(event) => onChangeMargin(event.target.value)}
        maxLength={BRIEF_FIELD_LIMITS.margin}
        autoComplete="off"
      />
    </div>
  </div>
);

const RadioPills = ({ name, options, value, onChange, groupKey, pillsClassName = '' }) => (
  <div className="brief-field">
    <div className="brief-field-label">{BRIEF_GROUP_LABELS[groupKey]}</div>
    <div className={`brief-pills${pillsClassName ? ` ${pillsClassName}` : ''}`}>
      {options.map((optionKey) => (
        <div className="brief-pill" key={optionKey}>
          <input
            type="radio"
            id={`${name}-${optionKey}`}
            name={name}
            checked={value === optionKey}
            onChange={() => onChange(optionKey)}
          />
          <label htmlFor={`${name}-${optionKey}`}>{getOptionPillLabel(optionKey)}</label>
        </div>
      ))}
    </div>
  </div>
);

const CheckboxPills = ({ options, values, onToggle, groupKey }) => (
  <div className="brief-field">
    <div className="brief-field-label">{BRIEF_GROUP_LABELS[groupKey]}</div>
    <div className="brief-pills">
      {options.map((optionKey) => (
        <div className="brief-pill" key={optionKey}>
          <input
            type="checkbox"
            id={`check-${optionKey}`}
            checked={values.includes(optionKey)}
            onChange={() => onToggle(optionKey)}
          />
          <label htmlFor={`check-${optionKey}`}>{getOptionPillLabel(optionKey)}</label>
        </div>
      ))}
    </div>
  </div>
);

const BriefGenerator = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(DEFAULT_BRIEF_FORM);
  const [showResult, setShowResult] = useState(false);
  const [copied, setCopied] = useState(false);

  const briefSections = useMemo(() => buildBriefViewModel(form), [form]);
  const plainText = useMemo(() => buildBriefPlainText(briefSections, BRIEF_TITLE), [briefSections]);

  const setField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: sanitizeBriefField(field, value),
    }));
  };

  const toggleListValue = (field, optionKey) => {
    setForm((current) => {
      const list = current[field];
      const next = list.includes(optionKey)
        ? list.filter((entry) => entry !== optionKey)
        : [...list, optionKey];

      return {
        ...current,
        [field]: next,
      };
    });
  };

  const goStep = (step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGenerate = () => {
    setShowResult(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRestart = () => {
    setForm(DEFAULT_BRIEF_FORM);
    setCurrentStep(1);
    setShowResult(false);
    setCopied(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const handleDownload = async () => {
    await downloadBriefDocx({
      title: BRIEF_TITLE,
      sections: briefSections,
      fileName: createBriefFileName(form),
    });
  };

  if (showResult) {
    return (
      <div className="brief-generator">
        <div className="brief-result-header">
          <div className="brief-result-title">
            Бриф <em>готов</em>
          </div>
          <div className="brief-result-actions">
            <button
              type="button"
              className={`brief-action-btn${copied ? ' brief-action-btn--copied' : ''}`}
              onClick={handleCopy}
            >
              <CopyIcon />
              {copied ? '✓ Скопировано!' : 'Скопировать'}
            </button>
            <button type="button" className="brief-action-btn" onClick={handleDownload}>
              <DownloadIcon />
              Скачать .docx
            </button>
          </div>
        </div>

        <div className="brief-brief-box">
          {briefSections.map((section) => (
            <section key={section.title}>
              <div className="brief-section-head">{section.title}</div>
              {section.rows.map((row) => (
                <div className="brief-row" key={`${section.title}-${row.label}`}>
                  <div className="brief-row-label">{row.label}</div>
                  <div className="brief-row-value">{row.value}</div>
                </div>
              ))}
            </section>
          ))}
        </div>

        <button type="button" className="brief-restart-btn" onClick={handleRestart}>
          ← Заполнить заново
        </button>
      </div>
    );
  }

  return (
    <div className="brief-generator">
      <div className="brief-progress">
        <div className="brief-progress-track">
          <div className="brief-progress-fill" style={{ width: `${getProgressPercent(currentStep)}%` }} />
        </div>
        <div className="brief-progress-label">Готовность к запуску</div>
      </div>

      {currentStep === 1 ? (
        <section className="brief-step">
          <StepHeader step={1} />
          <TextField fieldKey="NAME" value={form.name} onChange={(value) => setField('name', value)} />
          <TextField fieldKey="NICHE" value={form.niche} onChange={(value) => setField('niche', value)} />
          <TextField fieldKey="PRODUCT" value={form.product} onChange={(value) => setField('product', value)} multiline />
          <TextField fieldKey="USP" value={form.usp} onChange={(value) => setField('usp', value)} multiline />
          <TextField fieldKey="URL" value={form.url} onChange={(value) => setField('url', value)} />
          <TextField fieldKey="CITY" value={form.city} onChange={(value) => setField('city', value)} />
          <StepNav step={1} onBack={() => goStep(1)} onNext={() => goStep(2)} nextLabel="Далее →" />
        </section>
      ) : null}

      {currentStep === 2 ? (
        <section className="brief-step">
          <StepHeader step={2} />
          <div className="brief-two-col brief-two-col--audience">
            <TextField
              fieldKey="AGE"
              value={form.age}
              onChange={(value) => setField('age', value)}
              hideSublabel
            />
            <RadioPills
              name="gender"
              options={GENDER_OPTIONS}
              value={form.gender}
              onChange={(value) => setField('gender', value)}
              groupKey="GENDER"
              pillsClassName="brief-pills--inline"
            />
          </div>
          <TextField fieldKey="INTERESTS" value={form.interests} onChange={(value) => setField('interests', value)} multiline />
          <TextField fieldKey="PAIN" value={form.pain} onChange={(value) => setField('pain', value)} multiline />
          <TextField fieldKey="TRIGGERS" value={form.triggers} onChange={(value) => setField('triggers', value)} multiline />
          <TextField fieldKey="OBJECTIONS" value={form.objections} onChange={(value) => setField('objections', value)} />
          <StepNav step={2} onBack={() => goStep(1)} onNext={() => goStep(3)} nextLabel="Далее →" />
        </section>
      ) : null}

      {currentStep === 3 ? (
        <section className="brief-step">
          <StepHeader step={3} />
          <RadioPills
            name="experience"
            options={EXPERIENCE_OPTIONS}
            value={form.experience}
            onChange={(value) => setField('experience', value)}
            groupKey="EXPERIENCE"
          />
          <CheckboxPills
            options={PLATFORM_OPTIONS}
            values={form.platforms}
            onToggle={(value) => toggleListValue('platforms', value)}
            groupKey="PLATFORMS"
          />
          <TextField fieldKey="WORKED" value={form.worked} onChange={(value) => setField('worked', value)} multiline />
          <TextField fieldKey="FAILED" value={form.failed} onChange={(value) => setField('failed', value)} multiline />
          <RadioPills
            name="pixel"
            options={PIXEL_OPTIONS}
            value={form.pixel}
            onChange={(value) => setField('pixel', value)}
            groupKey="PIXEL"
          />
          <StepNav step={3} onBack={() => goStep(2)} onNext={() => goStep(4)} nextLabel="Далее →" />
        </section>
      ) : null}

      {currentStep === 4 ? (
        <section className="brief-step">
          <StepHeader step={4} />
          <RadioPills
            name="budget"
            options={BUDGET_OPTIONS}
            value={form.budget}
            onChange={(value) => setField('budget', value)}
            groupKey="BUDGET"
          />
          <TextField fieldKey="MAX_CPL" value={form.maxCpl} onChange={(value) => setField('maxCpl', value)} />
          <RadioPills
            name="scaleReady"
            options={SCALE_OPTIONS}
            value={form.scaleReady}
            onChange={(value) => setField('scaleReady', value)}
            groupKey="SCALE"
          />
          <RadioPills
            name="creativeBudget"
            options={CREATIVE_OPTIONS}
            value={form.creativeBudget}
            onChange={(value) => setField('creativeBudget', value)}
            groupKey="CREATIVE"
          />
          <StepNav step={4} onBack={() => goStep(3)} onNext={() => goStep(5)} nextLabel="Далее →" />
        </section>
      ) : null}

      {currentStep === 5 ? (
        <section className="brief-step">
          <StepHeader step={5} />
          <RadioPills
            name="goal"
            options={GOAL_OPTIONS}
            value={form.goal}
            onChange={(value) => setField('goal', value)}
            groupKey="GOAL"
          />
          <TextField
            fieldKey="MONTHLY_TARGET"
            value={form.monthlyTarget}
            onChange={(value) => setField('monthlyTarget', value)}
          />
          <MetricsField
            averageCheck={form.averageCheck}
            margin={form.margin}
            onChangeAverage={(value) => setField('averageCheck', value)}
            onChangeMargin={(value) => setField('margin', value)}
          />
          <CheckboxPills
            options={KPI_OPTIONS}
            values={form.kpis}
            onToggle={(value) => toggleListValue('kpis', value)}
            groupKey="KPIS"
          />
          <RadioPills
            name="deadline"
            options={DEADLINE_OPTIONS}
            value={form.deadline}
            onChange={(value) => setField('deadline', value)}
            groupKey="DEADLINE"
          />
          <StepNav step={5} onBack={() => goStep(4)} onNext={() => goStep(6)} nextLabel="Далее →" />
        </section>
      ) : null}

      {currentStep === 6 ? (
        <section className="brief-step">
          <StepHeader step={6} />
          <TextField fieldKey="CONTACT" value={form.contact} onChange={(value) => setField('contact', value)} />
          <RadioPills
            name="messenger"
            options={MESSENGER_OPTIONS}
            value={form.messenger}
            onChange={(value) => setField('messenger', value)}
            groupKey="MESSENGER"
          />
          <CheckboxPills
            options={MATERIAL_OPTIONS}
            values={form.materials}
            onToggle={(value) => toggleListValue('materials', value)}
            groupKey="MATERIALS"
          />
          <RadioPills
            name="landing"
            options={LANDING_OPTIONS}
            value={form.landing}
            onChange={(value) => setField('landing', value)}
            groupKey="LANDING"
          />
          <TextField fieldKey="EXTRA" value={form.extra} onChange={(value) => setField('extra', value)} multiline />
          <StepNav step={6} onBack={() => goStep(5)} onNext={handleGenerate} nextLabel="Сгенерировать бриф ✓" />
        </section>
      ) : null}
    </div>
  );
};

export default BriefGenerator;
